/**
 * features/expenses/ExpenseFormDialog.tsx — บันทึก/แก้ไขค่าใช้จ่าย
 *
 * จุดสำคัญ: ผู้ใช้กรอก "ยอดที่จ่ายจริง" แล้วเลือกว่ารวม VAT แล้วหรือยัง
 * แสดง preview การแยก base/VAT สดๆ ด้วย computeExpenseAmounts ตัวเดียวกับ server
 */

import { useEffect, useState } from 'react';
import { computeExpenseAmounts, expenseInputSchema, formatMoney, type ExpenseInput } from '@ft/core';
import { ApiError } from '~/lib/api';
import type { Expense } from '~/lib/types';
import { Dialog, useToast } from '~/ui/overlays';
import { Button, Field, Input, Select, Textarea } from '~/ui/primitives';
import { useExpenseCategories } from './hooks';
import { SupplierPicker } from './SupplierPicker';
import { useCreateExpense, useUpdateExpense } from './hooks';

interface FormState {
  date: string;
  title: string;
  inputAmount: string;
  inputIncludesVat: boolean;
  vatRate: number;
  categoryId: string;
  supplierId: string | null;
  supplierName: string;
  supplierTaxId: string;
  notes: string;
}

const EMPTY: FormState = {
  date: new Date().toISOString().slice(0, 10),
  title: '',
  inputAmount: '',
  inputIncludesVat: true,
  vatRate: 7,
  categoryId: '',
  supplierId: null,
  supplierName: '',
  supplierTaxId: '',
  notes: '',
};

export function ExpenseFormDialog({
  open,
  onClose,
  expense,
}: {
  open: boolean;
  onClose: () => void;
  expense: Expense | null;
}) {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof ExpenseInput, string>>>({});

  const { data: categories } = useExpenseCategories();
  const create = useCreateExpense();
  const update = useUpdateExpense();
  const toast = useToast();

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(
      expense
        ? {
            date: expense.date ?? new Date().toISOString().slice(0, 10),
            title: expense.title,
            inputAmount: String(expense.totalAmount),
            inputIncludesVat: true,
            vatRate: expense.vatRate,
            categoryId: expense.categoryId ?? '',
            supplierId: expense.supplierId,
            supplierName: expense.supplierName,
            supplierTaxId: expense.supplierTaxId,
            notes: expense.notes,
          }
        : EMPTY,
    );
  }, [open, expense]);

  const saving = create.isPending || update.isPending;
  const amount = Number.parseFloat(form.inputAmount) || 0;
  const preview = computeExpenseAmounts(amount, form.inputIncludesVat, form.vatRate);

  function patchForm(patch: Partial<FormState>) {
    setForm((current) => ({ ...current, ...patch }));
  }

  async function handleSubmit() {
    const input: ExpenseInput = {
      date: new Date(form.date),
      title: form.title,
      inputAmount: amount,
      inputIncludesVat: form.inputIncludesVat,
      vatRate: form.vatRate,
      categoryId: form.categoryId || null,
      supplierId: form.supplierId,
      supplierName: form.supplierName,
      notes: form.notes,
    };

    const parsed = expenseInputSchema.safeParse(input);
    if (!parsed.success) {
      const fieldErrors: Partial<Record<keyof ExpenseInput, string>> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof ExpenseInput;
        fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    try {
      if (expense) {
        await update.mutateAsync({ id: expense.id, input: parsed.data });
        toast.success('บันทึกค่าใช้จ่ายแล้ว');
      } else {
        await create.mutateAsync(parsed.data);
        toast.success('เพิ่มค่าใช้จ่ายแล้ว');
      }
      onClose();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'บันทึกไม่สำเร็จ');
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={expense ? 'แก้ไขค่าใช้จ่าย' : 'บันทึกค่าใช้จ่ายใหม่'}
      size="md"
      footer={
        <>
          <Button onClick={onClose} disabled={saving}>
            ยกเลิก
          </Button>
          <Button variant="primary" onClick={() => void handleSubmit()} loading={saving}>
            บันทึก
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="ชื่อรายการค่าใช้จ่าย" required error={errors.title}>
          <Input
            value={form.title}
            onChange={(event) => patchForm({ title: event.target.value })}
            invalid={Boolean(errors.title)}
            autoFocus
            placeholder="เช่น ค่าเช่าออฟฟิศ เดือนกันยายน"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="วันที่" required>
            <Input
              type="date"
              value={form.date}
              onChange={(event) => patchForm({ date: event.target.value })}
            />
          </Field>
          <Field label="หมวดหมู่">
            <Select value={form.categoryId} onChange={(event) => patchForm({ categoryId: event.target.value })}>
              <option value="">ไม่ระบุหมวดหมู่</option>
              {categories?.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <SupplierPicker
          supplierId={form.supplierId}
          supplierName={form.supplierName}
          taxId={form.supplierTaxId}
          onChange={(patch) =>
            patchForm({
              ...(patch.supplierId !== undefined ? { supplierId: patch.supplierId } : {}),
              ...(patch.supplierName !== undefined ? { supplierName: patch.supplierName } : {}),
              ...(patch.taxId !== undefined ? { supplierTaxId: patch.taxId } : {}),
            })
          }
        />

        <div className="rounded-md border border-border-base p-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="ยอดที่จ่ายจริง (บาท)" required error={errors.inputAmount}>
              <Input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={form.inputAmount}
                onChange={(event) => patchForm({ inputAmount: event.target.value })}
                invalid={Boolean(errors.inputAmount)}
                className="text-right"
              />
            </Field>
            <Field label="อัตรา VAT (%)">
              <Input
                type="number"
                inputMode="decimal"
                min="0"
                max="100"
                step="0.01"
                value={form.vatRate}
                onChange={(event) => patchForm({ vatRate: Number.parseFloat(event.target.value) || 0 })}
                className="text-right"
              />
            </Field>
          </div>

          <div className="mt-2 flex gap-4 text-sm">
            <label className="flex items-center gap-1.5">
              <input
                type="radio"
                checked={form.inputIncludesVat}
                onChange={() => patchForm({ inputIncludesVat: true })}
              />
              ยอดนี้รวม VAT แล้ว
            </label>
            <label className="flex items-center gap-1.5">
              <input
                type="radio"
                checked={!form.inputIncludesVat}
                onChange={() => patchForm({ inputIncludesVat: false })}
              />
              ยอดนี้ยังไม่รวม VAT
            </label>
          </div>

          {amount > 0 ? (
            <div className="mt-3 space-y-1 border-t border-border-base pt-2.5 text-sm">
              <div className="flex justify-between text-fg-muted">
                <span>มูลค่าก่อนภาษี</span>
                <span className="tabular text-fg">{formatMoney(preview.base)}</span>
              </div>
              <div className="flex justify-between text-fg-muted">
                <span>ภาษีมูลค่าเพิ่ม {preview.rate}%</span>
                <span className="tabular text-fg">{formatMoney(preview.vat)}</span>
              </div>
              <div className="flex justify-between font-medium text-fg">
                <span>ยอดรวมที่จ่าย</span>
                <span className="tabular">{formatMoney(preview.total)}</span>
              </div>
            </div>
          ) : null}
        </div>

        <Field label="หมายเหตุ">
          <Textarea value={form.notes} onChange={(event) => patchForm({ notes: event.target.value })} />
        </Field>
      </div>
    </Dialog>
  );
}
